/** Pagination helpers for Search results (page size + page window math). */

export const DEFAULT_SEARCH_PAGE_SIZE = 50;

export const SEARCH_PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

export const getSearchPageCount = (totalRows = 0, rowsPerPage = DEFAULT_SEARCH_PAGE_SIZE) => {
  const size = Math.max(1, Number(rowsPerPage) || DEFAULT_SEARCH_PAGE_SIZE);
  const total = Math.max(0, Number(totalRows) || 0);
  if (total === 0) return 1;
  return Math.ceil(total / size);
};

/**
 * Returns a short window of reachable 0-based page indexes around the current page.
 * Uses null entries as ellipsis placeholders when the range is truncated.
 */
export const getVisibleSearchPages = (page = 0, pageCount = 1, lastReachablePage = page) => {
  const current = Math.max(0, Number(page) || 0);
  const count = Math.max(1, Number(pageCount) || 1);
  const reachableLast = Math.min(
    count - 1,
    Math.max(current, Number(lastReachablePage) || 0),
  );
  if (reachableLast < 7) {
    return Array.from({ length: reachableLast + 1 }, (_, index) => index);
  }

  const pages = new Set([0, reachableLast, current]);
  for (let offset = 1; offset <= 2; offset += 1) {
    pages.add(current - offset);
    pages.add(current + offset);
  }

  const sorted = [...pages]
    .filter((index) => index >= 0 && index <= reachableLast)
    .sort((a, b) => a - b);

  const withEllipsis = [];
  sorted.forEach((index, position) => {
    if (position > 0 && index - sorted[position - 1] > 1) {
      withEllipsis.push(null);
    }
    withEllipsis.push(index);
  });
  return withEllipsis;
};

export const formatSearchResultsRange = ({
  page = 0,
  rowsPerPage = DEFAULT_SEARCH_PAGE_SIZE,
  totalRows = 0,
} = {}) => {
  const size = Math.max(1, Number(rowsPerPage) || DEFAULT_SEARCH_PAGE_SIZE);
  const total = Math.max(0, Number(totalRows) || 0);
  if (total === 0) return "0 of 0";

  const start = page * size + 1;
  const end = Math.min(total, (page + 1) * size);
  return `${start}–${end} of ${total}`;
};
